import React, { FC, useEffect } from "react";
import { Link } from "gatsby";
import classNames from "classnames";
import useMainContext from "../../hooks/useMainContext";
import Text, { TextSize, TextTone, TextWeight } from "../../components/Text";
import Label, { LabelSize, LabelTone } from "../../components/Label";
import { handleMouseUpByBlurring } from "kaleidoscope/src/utils/focus";
import { Transition } from "react-transition-group";
import { AnimationDuration } from "kaleidoscope/src/styles/Animations";
import useWindowSize from "../../hooks/useWindowSize";
import forceReflow from "kaleidoscope/src/utils/forceReflow";
import { PostGroup } from "../../utils/posts";

interface SidebarProps {
  className?: string;
  navItems: PostGroup[];
}

const Sidebar: FC<SidebarProps> = ({ className, navItems }) => {
  const { pathname } = useMainContext();

  return (
    <SidebarWrapper>
      {navItems.map(({ label, pages }) => (
        <SidebarCategory label={label} key={label}>
          {pages.map(({ label, url }) => (
            <SidebarLink key={url} url={url} current={url === pathname || `${url}/` === pathname}>
              {label}
            </SidebarLink>
          ))}
        </SidebarCategory>
      ))}
    </SidebarWrapper>
  );
};

interface SidebarWrapperProps {
  className?: string;
}

export const SidebarWrapper: FC<SidebarWrapperProps> = ({ className, children }) => {
  const { sidebarOpen, setHasSidebar } = useMainContext();
  const { width } = useWindowSize();
  const isMobile = width < 1024;

  useEffect(() => {
    setHasSidebar(true);

    return () => {
      setHasSidebar(false);
    };
  }, []);

  return (
    <Transition
      mountOnEnter
      unmountOnExit
      onEnter={forceReflow}
      in={!isMobile || sidebarOpen}
      timeout={AnimationDuration.Long}
    >
      {(status) => (
        <div className={classNames("kld-sidebar", `kld-sidebar--${status}`, className)}>
          <nav className="kld-sidebar__nav">{children}</nav>
        </div>
      )}
    </Transition>
  );
};

interface SidebarLinkProps {
  url: string;
  current: boolean;
}

export const SidebarLink: FC<SidebarLinkProps> = ({ url, current, children }) => {
  const { setSidebarOpen } = useMainContext();

  return (
    <Link
      key={url}
      className="kld-sidebar__nav-link"
      to={url}
      aria-current={current}
      onMouseUp={handleMouseUpByBlurring}
      onClick={() => setSidebarOpen(false)}
    >
      <Text size={TextSize.Large} weight={TextWeight.Strong} tone={TextTone.Auto}>
        {children}
      </Text>
    </Link>
  );
};

interface SidebarCategoryProps {
  label?: string;
}

export const SidebarCategory: FC<SidebarCategoryProps> = ({ children, label }) => {
  return (
    <div className="kld-sidebar__nav-group" key={label}>
      {!!label && label !== "_uncategorized" && (
        <Label as="span" className="kld-sidebar__nav-group-label" size={LabelSize.Small} tone={LabelTone.Secondary}>
          {label}
        </Label>
      )}
      {children}
    </div>
  );
};

export default Sidebar;
